import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserId } from 'flowhawk-core';
import { teamRouteSchema } from '../../../../Utils/routeSchema';
import type { RootState } from '../../../../Redux';
import { updateTeam, deleteTeam } from '../../../../Redux/TeamActions';
import CreateEditTeam from '../../../../Components/CreateEditTeam';
import type { Team, UserProfile } from '../../../../Utils/types';
import { useSocketStatus } from '../../../../Selectors/hooks';

export const Route = createFileRoute('/(main)/_layout/teams/_teams/$teamId')({
  params: {
    parse: (params) => teamRouteSchema.parse(params),
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { teamId } = Route.useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isConnected } = useSocketStatus();

  const teams = useSelector((state: RootState) => state.team.teams || []);
  const teamsLoading = useSelector((state: RootState) => state.loading.teams || false);
  const saveTeamLoading = useSelector((state: RootState) => state.loading.saveTeam || false);
  const authState = useSelector((state: RootState) => state.auth);
  const profile = authState.profile;
  const memberId = selectUserId(authState);
  const memberAccessLevel = authState.accessLevel || '';
  const contactsMap = useSelector((state: RootState) => state.chatroom.contactsMap || {});
  const contactsOrder = useSelector((state: RootState) => state.chatroom.contactsOrder || []);
  const contactsAreLoaded = useSelector((state: RootState) => state.chatroom.contactsAreLoaded || false);
  const newUserInvited = useSelector((state: RootState) => state.chatroom.newUserInvited || false);

  const team = useMemo(() => teams.find((t: Team) => t.teamId === teamId), [teams, teamId]);

  const isOrgManagerPlus =
    memberAccessLevel === 'OrgManager' || memberAccessLevel === 'Sales' || memberAccessLevel === 'SysAdmin';
  const canEdit = isOrgManagerPlus;
  const canDelete = isOrgManagerPlus || (team?.rights?.delete ?? false);

  const handleClose = useCallback(() => {
    navigate({ to: '/teams' });
  }, [navigate]);

  const handleUpdate = useCallback(
    (teamId: number, teamName: string, teamMembers: UserProfile[], shared: boolean) => {
      dispatch(updateTeam(teamId, teamName, teamMembers, shared));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (teamId: number) => {
      if (!team) return;
      const delPrefix = team.shared
        ? 'This team is shared across the Organization and deleting it will remove the team for all Organization members.\n\n'
        : '';
      const response = window.confirm(
        `${delPrefix}Are you sure you wish to delete the team '${team.teamName}'. You cannot undo this?`,
      );
      if (response) {
        dispatch(deleteTeam(teamId));
      }
    },
    [dispatch, team],
  );

  if (!team) {
    return (
      <div className="chat-main-empty">
        <p>Team not found</p>
      </div>
    );
  }

  return (
    <div className="team-detail-content">
      <CreateEditTeam
        isConnected={isConnected}
        teams={teams}
        teamsLoading={teamsLoading}
        contactsMap={contactsMap}
        contactsOrder={contactsOrder}
        contactsAreLoaded={contactsAreLoaded}
        memberId={memberId}
        team={team}
        canEdit={canEdit}
        canDelete={canDelete}
        newUserInvited={newUserInvited}
        saveTeamLoading={saveTeamLoading}
        onCreate={() => {}}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onClose={handleClose}
      />
    </div>
  );
}
